import React, { useMemo } from 'react';
import DataSection from './DataSection';
import styles from '../TradeFlowTrackingTab.module.css';
import {
  TechnicalGateDecision,
  TechnicalGateDecisionsParams,
  getTechnicalGateDecisions,
} from '../../../services/tradeFlowAdminApi';
import { useFlowData } from '../hooks/useTradeFlowData';

interface TechnicalGateSectionProps {
  dateFrom: string;
  dateTo: string;
  symbol: string;
}

const TechnicalGateSection: React.FC<TechnicalGateSectionProps> = ({ dateFrom, dateTo, symbol }) => {
  const params = useMemo<TechnicalGateDecisionsParams>(() => ({
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    symbol: symbol || undefined,
  }), [dateFrom, dateTo, symbol]);

  const { data, total, page, setPage, isLoading, isFetching, isError, error, totalPages } = useFlowData<TechnicalGateDecision, TechnicalGateDecisionsParams>({
    queryKeyBase: ['admin', 'trade-flow', 'technical'],
    fetchFn: (p) => getTechnicalGateDecisions(p).then(r => r),
    params,
    refetchInterval: 10000,
  });

  return (
    <DataSection
      title="Enhanced Technical Gate"
      tabNumber={9}
      description="RSI, MACD, EMA, Bollinger Bands, and ADX indicator evaluations that determine if a trade's technical setup is favorable."
      loading={isLoading}
      isFetching={isFetching}
      error={error?.message || null}
      dataCount={data.length}
      page={page}
      totalPages={totalPages}
      total={total}
      onPageChange={setPage}
    >
      <table className={styles.dataTable}>
        <thead>
          <tr>
            <th>Time</th>
            <th>Symbol</th>
            <th>Rec ID</th>
            <th>Indicator</th>
            <th>Value</th>
            <th>Signal</th>
            <th>Result</th>
          </tr>
        </thead>
        <tbody>
          {data.map((d) => (
            <tr key={d.id}>
              <td className={styles.monoSmall}>
                {d.evaluated_at ? new Date(d.evaluated_at).toLocaleString() : '-'}
              </td>
              <td className={styles.mono}>{d.symbol}</td>
              <td className={styles.monoSmall}>{d.recommendation_id.slice(0, 12)}...</td>
              <td className={styles.mono}>{d.indicator}</td>
              <td className={styles.mono}>
                {d.value !== null ? d.value.toFixed(4) : '-'}
              </td>
              <td>{d.signal || '-'}</td>
              <td>
                <span className={d.passed ? styles.badgePass : styles.badgeFail}>
                  {d.passed ? 'PASS' : 'FAIL'}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </DataSection>
  );
};

export default TechnicalGateSection;
